import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './button';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'default';
  loading?: boolean;
  onConfirm: () => void | Promise<void>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  tone = 'default',
  loading,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-[rgba(20,16,8,0.40)] backdrop-blur-[2px]',
            'data-[state=open]:animate-fade-in',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[min(92vw,420px)]',
            'rounded-3 border border-card-stroke bg-card shadow-clay p-6',
            'data-[state=open]:animate-fade-in',
          )}
        >
          <div className="flex items-start gap-3">
            {tone === 'danger' && (
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-warn-soft text-warn">
                <AlertTriangle className="h-5 w-5" strokeWidth={1.8} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <DialogPrimitive.Title className="font-display text-[18px] font-bold tracking-[-0.015em] text-fg leading-tight">
                {title}
              </DialogPrimitive.Title>
              {description && (
                <DialogPrimitive.Description className="text-sm text-fg-muted mt-1.5">
                  {description}
                </DialogPrimitive.Description>
              )}
            </div>
          </div>
          <div className="mt-6 flex items-center justify-end gap-2">
            <Button variant="ghost" type="button" onClick={() => onOpenChange(false)} disabled={loading}>
              {cancelLabel}
            </Button>
            <Button
              variant={tone === 'danger' ? 'danger' : 'primary'}
              type="button"
              onClick={() => void onConfirm()}
              disabled={loading}
            >
              {confirmLabel}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
